import React from "react";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useCallback } from "react";
import { formatPhoneNumber } from "react-phone-number-input";
import { Checkbox, FormControlLabel } from "@mui/material";
import { array } from "zod";
import { Company, NotificationTriggerType } from "generated/graphql";
import ProjectFileExplorerInput from "modules/ProjectFiles/ProjectFileExplorerInput";
import useUpdateCompanyProjectFileFolderId from "hooks/companies/useUpdateCompanyProjectFileFolderId";

interface InfoItemProps {
  item: { label: string; value: any };
  company?: Company;
  direction?: "row" | "column";
  dense?: boolean;
  denseSize?: "small" | "medium";
  notificationLabel?: string;
}

const notificationTypes = Object.values(NotificationTriggerType);

export const InfoItem: React.FC<InfoItemProps> = ({
  item,
  direction = "row",
  company,
  dense = false,
  denseSize = "small",
  notificationLabel,
}) => {
  const { label, value } = item;
  const [updateCompanyProjectFileFolderId] = useUpdateCompanyProjectFileFolderId();
  const isNotificationTypes = label.toLowerCase() === "notification types";
  const shouldShowLabel = label !== "Project files folder id" && !(isNotificationTypes && notificationLabel === "");
  const normalizedValue = Array.isArray(value) ? value[0] : value;

  const modifiedPhoneNumber = useCallback(
    (phone) => {
      const prependCode = phone.substring(0, 2) === "+1" ? phone : `+1${phone}`;
      return formatPhoneNumber(prependCode);
    },
    [item]
  );

  const updateObjToArray = (object) => {
    let notificationsTypeOptions = [];
    if (object) {
      const entriesArray = Object.entries(object);
      const filteredArray = entriesArray.filter(([key]) => key !== "__typename");
      notificationsTypeOptions = filteredArray;
    }
    return notificationsTypeOptions;
  };
  const handleProjectFilesFolderSelect = async (folderId: string) => {
    await updateCompanyProjectFileFolderId({
      variables: {
        id: company?.id,
        projectFilesFolderId: folderId,
      },
    });
  };

  const renderCheckboxes = () => {
    const notificationsTypeOptions = updateObjToArray(normalizedValue);
    if (notificationsTypeOptions.length === 0) {
      return notificationTypes.map((type) => (
        <FormControlLabel
          key={type}
          control={<Checkbox name={type.toLowerCase()} checked={false} />}
          label={type
            .replace("_", " ")
            .toLowerCase()
            .replace(/(?:^|\s)\S/g, (a) => a.toUpperCase())}
        />
      ));
    } else {
      return notificationsTypeOptions.map((value) => (
        <FormControlLabel
          key={value[0]}
          control={<Checkbox name={value[0]} checked={value[1] == true ? true : false} />}
          label={value[0]}
        />
      ));
    }
  };

  const isMediumDense = dense && denseSize === "medium";
  const densePy = isMediumDense ? 0.5 : 0.25;
  const denseVariant = isMediumDense ? "button" : "caption";

  return (
    <MDBox display="flex" py={dense ? densePy : 1} pr={2} flexDirection={direction}>
      {shouldShowLabel && (
        <MDTypography
          variant={dense ? denseVariant : "button"}
          fontWeight="bold"
          textTransform="capitalize"
        >
          {isNotificationTypes && notificationLabel ? notificationLabel : label}
          {direction === "row" && !isNotificationTypes ? ":" : ""}&nbsp;
        </MDTypography>
      )}
      {!isNotificationTypes && label !== "Project files folder id" && (
        <MDTypography variant={dense ? denseVariant : "button"} fontWeight="regular" color="text">
          {label === "Phone" ? modifiedPhoneNumber(value[0]) : value}
        </MDTypography>
      )}{" "}
      {label === "Project files folder id" && (
        <ProjectFileExplorerInput
          submitFn={handleProjectFilesFolderSelect}
          folderId={company.projectFilesFolderId}
          showLabel
        />
      )}
      {isNotificationTypes && (
        <MDBox display="flex" alignItems="center" gap={2} flexWrap="wrap">
          {renderCheckboxes()}
        </MDBox>
      )}
    </MDBox>
  );
};
